import type { Feed } from '@/types/feed.type'
import type { PaginateResponse } from '@/utils/paginate'

import { useState } from 'react'
import { paths } from '@/routes/paths'
import { Card } from '@/components/ui/card'
import axios, { endpoints } from '@/lib/axios'
import { useBoolean } from '@/hooks/use-boolean'
import { Skeleton } from '@/components/ui/skeleton'
import { useRouter } from '@/routes/hooks/use-router'
import { useDeepEffect } from '@/hooks/use-deep-effect'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useSearchParams } from '@/routes/hooks/use-search-params'
import { useAuthContext } from '@/contexts/auth-context/auth-provider'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

import FeedCard from '../feed-card'
import FeedNavTabs from '../feed-nav-tabs'

export default function FeedView() {
  const router = useRouter()
  const [feeds, setFeeds] = useState<Feed[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'feed' | 'profile'>('feed')

  const searchParams = useSearchParams()
  const type = searchParams.get('type')

  const { user } = useAuthContext()

  const reload = useBoolean()
  const firstInit = useBoolean(true)

  // เพิ่มฟังก์ชัน getFeedById
  const getFeedById = async (feedId: string) => {
    try {
      const { data } = await axios.get<Feed>(endpoints.feeds.byId(feedId))
      // อัพเดท feed ใน feeds array
      setFeeds((prevFeeds) =>
        prevFeeds.map((feed) => (feed.id === feedId ? data : feed)),
      )
    } catch (error) {
      console.error('Error fetching feed:', error)
    }
  }

  const fetchFeeds = async (pageNumber = 1, append = false, type?: string) => {
    setLoading(pageNumber === 1)
    try {
      let tabType = tab
      if (type === 'feed' || type === 'profile') {
        if (firstInit.value) {
          setTab(type)
          if (type === 'feed') {
            reload.onToggle()
          }
          return
        }

        tabType = type
      }

      const { data } = await axios.get<PaginateResponse<Feed>>(
        // tab === 'timeline' ? endpoints.feeds.timeline : endpoints.feeds.list,
        endpoints.feeds.list,
        {
          params: {
            page: pageNumber,
            limit: 10,
            orderBy: 'createdAt:desc',
            ...(tabType === 'profile' && user && { userId: user?.uid }),
          },
        },
      )
      setFeeds((prev) => (append ? [...prev, ...data.items] : data.items))
      setHasMore(pageNumber * data.limit < data.total)
    } catch {
      // TODO: handle error (toast)
      setHasMore(false)
    } finally {
      setLoading(false)
      firstInit.onFalse()
    }
  }

  useDeepEffect(() => {
    fetchFeeds(1, false, type ?? undefined)
  }, [tab, reload.value, type])

  const fetchMoreData = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchFeeds(nextPage, true)
    }
  }

  const name = user
    ? user.firstName
      ? `${user.firstName} ${user.lastName}`
      : user.displayName
    : ''

  return (
    <div className="max-w-lg mx-auto py-4 px-3 space-y-6">
      <FeedNavTabs value={tab} onChange={setTab} />

      <Card
        className="p-4 flex flex-row bg-white border border-gray-200 items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors shadow-sm"
        onClick={() => router.push(paths.feedCreate)}
      >
        <div className="w-10 h-10 rounded-full flex items-center justify-center">
          <Avatar className="size-12 ring-2 border-2 border-transparent ring-blue-500">
            <AvatarImage
              src={
                user?.role !== 'ADMIN' ? user?.imageUrl : '/images/rfc-logo.png'
              }
              alt={name || ''}
              className="object-cover"
            />
            {!!name && (
              <AvatarFallback className="size-12 bg-gray-100 text-gray-600">
                {user?.role !== 'ADMIN' ? name.slice(0, 2) : 'RFC'}
              </AvatarFallback>
            )}
          </Avatar>
        </div>
        <div className="bg-transparent border-2 border-gray-300 h-10 rounded-lg w-full shrink-[20] flex items-center px-4 text-start text-gray-600 placeholder-gray-500">
          คุณกำลังคิดอะไรอยู่...
        </div>
      </Card>

      {loading ? (
        <div className="max-w-lg mx-auto">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-50 w-full rounded-xl" />
            ))}
          </div>
        </div>
      ) : (
        <>
          <InfiniteScroll
            dataLength={feeds.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <div className="text-center">
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-50 w-full rounded-xl" />
                  ))}
                </div>
              </div>
            }
            endMessage={
              <>
                {!!feeds.length && (
                  <div className="text-center py-4 text-gray-400">
                    <p>•</p>
                    <p>•</p>
                    <p>ไม่มีโพสต์เพิ่มเติม</p>
                  </div>
                )}
              </>
            }
          >
            {feeds.length === 0 ? (
              <div className="flex flex-col py-8 gap-2 items-center justify-center">
                <div className="text-center text-gray-400">•</div>
                <div className="text-center text-gray-400">•</div>
                <div className="text-center text-gray-400">ยังไม่มีโพสต์</div>
              </div>
            ) : (
              feeds.map((feed) => (
                <FeedCard
                  key={feed.id}
                  feed={feed}
                  getFeedById={getFeedById}
                  reload={() => {
                    reload.onToggle()
                    document.documentElement.scrollTop = 0
                  }}
                />
              ))
            )}
          </InfiniteScroll>
        </>
      )}
    </div>
  )
}
